import { AfterContentChecked, ChangeDetectorRef, Component, ContentChild, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, DestroyRef, inject} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { debounceTime } from 'rxjs'
import { TranslateService } from '@ngx-translate/core'
import { CommonService } from '../../services/common.service'
import { DateService } from '../../services/date.service'
import { ComponentState, timeRange } from '@app/models/shared.model'
import { DatePipe } from '@angular/common'
import { isDate, pick } from 'lodash-es'
import { InputMaskService } from '../../services/input-mask.service'
import { WorkPeriod } from '@app/models/order.model'
import { DynamicFormControlValidatorService } from '@app/shared/services/dynamic-form-control-validator.service'

@Component({selector: 'app-restrictions',
  templateUrl: './restrictions.component.html',
  styleUrls: ['./restrictions.component.scss'],
  standalone: false,
})
export class RestrictionsComponent implements OnInit, OnChanges, AfterContentChecked {
  private destroyRef = inject(DestroyRef)
  /** Other content template reference */
  @ContentChild('other') otherRender: TemplateRef<any>

  /** Selected restrictions */
  @Input() selectedRestrictions: any[] = []

  /** Display component state edit/create/details */
  @Input() state: ComponentState

  /** Panel style */
  @Input() style: any

  /** Form group */
  @Input() form: FormGroup

  /** TimeLimitation object */
  @Input() timeLimitationsObject: any

  /** TimeLimitation key */
  @Input() timeLimitationsKey: string

  /** Use the dates range instead the days k */
  @Input() useWorkPeriod: boolean

  /** Show the work period estimated fee */
  @Input() workPeriodEstimatedFeeShow: boolean

  /** Inputs class */
  @Input() inputsClass: string = 'p-input-filled'

  /** Enable one restriction only and hide the add button */
  @Input() enableOneRestrictionOnly: boolean

  /** The control name that we */
  @Input() restrictionSaveKey = 'entryTimes'

  /** Work period list using when the "use work period" enabled */
  workPeriod: WorkPeriod[] = []

  /** Times list */
  dayList: any[]

  /** Restriction ID for validate check */
  restrictionId: number

  /** Model dates object for bind to "invitation_calendar" component */
  modelDates: any = {}

  constructor(
    private fb: FormBuilder,
    private commonService: CommonService,
    private dateService: DateService,
    private datePipe: DatePipe,
    private maskService: InputMaskService,
    private cdref: ChangeDetectorRef,
    private validatorsService: DynamicFormControlValidatorService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.restrictionId = this.selectedRestrictions?.length || 0
    this.dayList = this.commonService.getDayList(0, true)

    this.modelChanged()
    if (this.state == 'create' && this.enableOneRestrictionOnly) this.addNewRestriction(null, null)

    if (this.selectedRestrictions && this.selectedRestrictions.length > 0) {
      this.selectedRestrictions.forEach((restriction) => {
        restriction.daysWeek = this.mapDaysWeek(restriction.daysWeek)
        this.createFormRestriction(restriction)
      })
    }
  }

  /**
   * Maps day values to day objects for daysWeek
   * @param daysWeek Array of day values or objects
   * @returns Array of day objects from dayList
   */
  mapDaysWeek(daysWeek: any[]): any[] {
    return daysWeek
      .map((day) => {
        if (day && typeof day.value !== 'undefined') {
          // Already a day object
          return day
        } else {
          // Map day value to day object
          const dayObj = this.dayList.find((d) => d.value === day)
          if (dayObj) {
            return dayObj
          } else {
            console.warn('Day value not found in dayList:', day)
            return null // Or handle as needed
          }
        }
      })
      .filter((day) => day !== null) // Remove any nulls
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['state']?.currentValue == 'details' || changes['state']?.currentValue == 'edit') {
      this.selectedRestrictions = this.dateService.buildAndGetRestrictionsByTimeLimitation(this.timeLimitationsObject, this.timeLimitationsKey)
      if (this.useWorkPeriod) this.selectedRestrictions = this.form.get('work_periods')!.value
    }
    this.modelChanged()
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges()
  }


  /**
   * When create restriction update model
   */
  modelChanged() {
    if (this.selectedRestrictions) {
      for (const restriction of this.selectedRestrictions) {
        this.createFormRestriction(restriction)
        if (this.useWorkPeriod) this.modelDates = pick(restriction.formControl.value, 'from', 'to')
      }
    }
  }

  /**
   * Create form restriction
   * @param restriction
   */
  createFormRestriction(restriction: any) {
    let formRestriction: FormGroup

    // Map daysWeek to ensure it contains day objects
    restriction.daysWeek = this.mapDaysWeek(restriction.daysWeek)

    if (!this.useWorkPeriod) {
      formRestriction = this.fb.group({
        daysWeek: [restriction.daysWeek.map((d: any) => d.value) || '', [Validators.required]],
        hourFirstRange: [{ value: restriction.hourFirstRange, disabled: false }, [Validators.required]],
        hourSecondRange: [{ value: restriction.hourSecondRange, disabled: false }, [Validators.required]],
      })
      restriction.daysWeek = formRestriction.get('daysWeek')!.value
    } else {
      formRestriction = this.fb.group(
        {
          from: [
            {
              value: restriction.from ? this.datePipe.transform(restriction.from, 'yyyy/MM/dd') : '',
              disabled: false,
            },
            [Validators.required],
          ],
          to: [
            {
              value: restriction.to ? this.datePipe.transform(restriction.to, 'yyyy/MM/dd') : '',
              disabled: false,
            },
            [Validators.required],
          ],
          daily_work_from: [{ value: restriction.daily_work_from, disabled: false }, [Validators.required]],
          daily_work_to: [{ value: restriction.daily_work_to, disabled: false }, [Validators.required]],
        },
        {
          validator: this.validatorsService.timeRangeValidator('daily_work_from', 'daily_work_to', 'from', 'to'),
        }
      )
    }

    for (const controlName in formRestriction.controls) {
      formRestriction
        .get(controlName)!
        .valueChanges.pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
        .subscribe((value: any[] | any) => {
          if (!this.useWorkPeriod) {
            if (isDate(value) && !this.useWorkPeriod) {
              restriction[controlName] = this.datePipe.transform(new Date(value.toString()), 'HH:mm')
            } else {
              if (controlName == 'hourFirstRange' || controlName == 'hourSecondRange' || controlName == 'daily_work_from' || controlName == 'daily_work_to') {
                const start = !this.useWorkPeriod ? 'hourFirstRange' : 'daily_work_from'
                const end = !this.useWorkPeriod ? 'hourSecondRange' : 'daily_work_to'

                if (!/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(value) || !this.maskService.validateTime(value)) {
                  formRestriction?.get(controlName)!.setErrors(['Not valid input'])
                } else {
                  if (
                    formRestriction.get(start)?.value?.toString() &&
                    formRestriction.get(end)?.value?.toString() &&
                    this.maskService.maskTimeValidateFirsTimeGreaterThanSecond(formRestriction.get(start)?.value?.toString(), formRestriction.get(end)?.value?.toString())
                  ) {
                    restriction[start] = isDate(formRestriction.get(start)!.value)
                      ? this.datePipe.transform(formRestriction.get(start)!.value, 'HH:mm')
                      : formRestriction.get(start)!.value
                    restriction[end] = isDate(formRestriction.get(end)!.value) ? this.datePipe.transform(formRestriction.get(end)!.value, 'HH:mm') : formRestriction.get(end)!.value
                    formRestriction?.get(start)!.setErrors(null)
                    formRestriction?.get(end)!.setErrors(null)
                  } else if (
                    this.maskService.maskTimeValidateFirsTimeGreaterThanSecond(formRestriction.get(start)?.value?.toString(), formRestriction.get(end)?.value?.toString())
                  ) {
                    formRestriction?.get(controlName)!.setErrors(['Not valid input'])
                  } else {
                    restriction[controlName] = value
                    formRestriction?.get(controlName)!.setErrors(['Not valid input'])
                  }
                }
              }
            }
            if (!this.hasCollision(restriction, value) && formRestriction.valid) {
              this.form.get(this.restrictionSaveKey)!.setValue(this.selectedRestrictions)
              this.form.get(this.restrictionSaveKey)!.clearValidators()
              this.form.get(this.restrictionSaveKey)!.setErrors(null)
            } else {
              if (this.useWorkPeriod && !value) this.form.get(this.restrictionSaveKey)!.setValue('')

              // Set more specific error message based on collision details
              const collisionDetails = this.getCollisionDetails()
              const errorMessage = collisionDetails.length > 0 ? `Time restrictions overlap: ${collisionDetails[0].timeOverlap}` : 'Invalid time restriction input'

              this.form.get(this.restrictionSaveKey)!.setErrors([errorMessage])
            }
          } else {
            if (formRestriction.valid && this.validatorsService.isTimeValid(restriction.formControl, 'from', 'to')) {
              this.form.get(this.restrictionSaveKey)!.setErrors(null)
              this.form.get(this.restrictionSaveKey)!.setValue([restriction])
            } else {
              this.form.get(this.restrictionSaveKey)!.setErrors(['not_valid_work_periods'])
              this.form.get(this.restrictionSaveKey)!.setValue(null)
            }
          }
          restriction.formControl = formRestriction
        })
    }
    restriction.formControl = formRestriction

    this.form
      .get(this.restrictionSaveKey)!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value === '') {
          this.form.controls[this.restrictionSaveKey].addValidators([Validators.required])
        }
      })
  }
  /**
   * Has collision check if restrictions has collision
   * @param restriction The restriction to check for collisions
   * @param value Optional value parameter (currently unused but kept for compatibility)
   * @returns true if collision exists, false otherwise
   */
  hasCollision(restriction: timeRange, _value?: any): boolean {
    // Need at least 2 restrictions to have a collision
    if (!this.selectedRestrictions || this.selectedRestrictions.length < 2) {
      return false
    }

    const currentRestriction = this.normalizeRestriction(restriction)
    const otherRestrictions = this.selectedRestrictions.filter((res) => res.restrictionId !== currentRestriction.restrictionId).map((res) => this.normalizeRestriction(res))

    // Check for collisions with each other restriction
    for (const otherRestriction of otherRestrictions) {
      if (this.restrictionsHaveTimeConflict(currentRestriction, otherRestriction)) {
        return true
      }
    }

    return false
  }

  /**
   * Normalize a restriction object to handle "everyday" selections and ensure consistent format
   * @param restriction The restriction to normalize
   * @returns Normalized restriction with expanded days
   */
  private normalizeRestriction(restriction: timeRange): timeRange {
    const normalized = { ...restriction }

    // Handle "everyday" (-1) selection by expanding to all days
    if (normalized.daysWeek?.length === 1 && normalized.daysWeek[0].value === -1) {
      normalized.daysWeek = this.dayList?.filter((day) => day.value !== -1) || []
    }

    return normalized
  }

  /**
   * Check if two restrictions have overlapping time periods on shared days
   * @param restriction1 First restriction
   * @param restriction2 Second restriction
   * @returns true if there's a time conflict, false otherwise
   */
  private restrictionsHaveTimeConflict(restriction1: timeRange, restriction2: timeRange): boolean {
    // Check if restrictions share any common days
    if (!this.restrictionsShareDays(restriction1, restriction2)) {
      return false
    }

    // Convert time ranges to Date objects for comparison
    const time1Start = this.getTimeAsDate(restriction1.hourFirstRange)
    const time1End = this.getTimeAsDate(restriction1.hourSecondRange)
    const time2Start = this.getTimeAsDate(restriction2.hourFirstRange)
    const time2End = this.getTimeAsDate(restriction2.hourSecondRange)

    // Check if any of the time conversions failed
    if (!time1Start || !time1End || !time2Start || !time2End) {
      return false
    }

    // Check for time overlap (adjacent times are allowed)
    return this.dateService.dateRangeOverlaps(time1Start, time1End, time2Start, time2End)
  }
  /**
   * Check if two restrictions share any common days
   * @param restriction1 First restriction
   * @param restriction2 Second restriction
   * @returns true if they share days, false otherwise
   */
  private restrictionsShareDays(restriction1: timeRange, restriction2: timeRange): boolean {
    if (!restriction1.daysWeek?.length || !restriction2.daysWeek?.length) {
      return false
    }

    const days1 = restriction1.daysWeek.map((day: any) => (typeof day === 'object' ? day.value : day))
    const days2 = restriction2.daysWeek.map((day: any) => (typeof day === 'object' ? day.value : day))

    return days1.some((day: any) => days2.includes(day))
  }

  /**
   * Convert time string or Date to a standardized Date object for comparison
   * @param timeValue Time as string (HH:mm) or Date object
   * @returns Date object or null if conversion fails
   */
  private getTimeAsDate(timeValue: string | Date): Date | null {
    if (!timeValue) {
      return null
    }

    if (isDate(timeValue)) {
      return timeValue as Date
    }

    try {
      return this.dateService.convertTimeFromStringNormal(timeValue as string, new Date())
    } catch (error) {
      console.warn('Failed to convert time value:', timeValue, error)
      return null
    }
  }

  /**
   * Add new restriction
   * @param e For prevent default
   */
  addNewRestriction(e: Event | null, existRestriction?: any) {
    e?.preventDefault()
    this.restrictionId = this.selectedRestrictions?.length || 0
    // When the time is 23 get current hour - 1
    const defaultFirstRange = new Date().getHours() == 23 ? new Date(new Date().setHours(new Date().getHours() - 1)) : new Date()
    const defaultSecondRange = new Date(new Date().setHours(defaultFirstRange.getHours() + 1))

    let restriction: timeRange | WorkPeriod | any
    if (!this.useWorkPeriod) {
      restriction = {
        restrictionId: ++this.restrictionId,
        daysWeek: !existRestriction ? this.dayList.filter((val) => val.value == -1) : existRestriction.daysWeek,
        hourFirstRange: !existRestriction ? defaultFirstRange : existRestriction.hourFirstRange,
        hourSecondRange: !existRestriction ? defaultSecondRange : existRestriction.hourSecondRange,
      }
    } else {
      restriction = {
        from: defaultFirstRange,
        to: defaultFirstRange,
        daily_work_from: this.datePipe.transform(defaultFirstRange, 'HH:mm'),
        daily_work_to: this.datePipe.transform(defaultSecondRange, 'HH:mm'),
      }
      if (this.workPeriodEstimatedFeeShow) restriction['estimated_fee'] = NaN
    }

    if (existRestriction) this.selectedRestrictions.push(restriction)
    else this.selectedRestrictions.push(restriction)
    this.createFormRestriction(restriction)

    if (this.useWorkPeriod) this.modelDates = pick(restriction.formControl.value, 'from', 'to')
  }

  /**
   * Get restriction form control by key
   * @param item Control
   * @param key Key
   * @returns Form control
   */
  getRestrictionFormControl(restriction: any, key: string): FormControl {
    return restriction.formControl?.get(key) as FormControl
  }

  /**
   * Delete restriction
   * @param e For prevent default
   * @param restriction Restriction to delete
   */
  onDeleteRestriction(e: Event, restriction: any) {
    const index = this.selectedRestrictions.indexOf(restriction)
    this.selectedRestrictions.splice(index, 1)
    this.form.get(this.restrictionSaveKey)!.setValue(this.selectedRestrictions)

    if (!this.selectedRestrictions.length) {
      this.form?.controls[this.restrictionSaveKey].clearValidators()
      this.form?.controls[this.restrictionSaveKey].updateValueAndValidity()
    }
    this.selectedRestrictions.forEach((element) => {
      if (!this.hasCollision(element, null)) {
        this.form.get(this.restrictionSaveKey)!.setValue(this.selectedRestrictions)
        this.form.get(this.restrictionSaveKey)!.clearValidators()
      } else this.form.get(this.restrictionSaveKey)!.setErrors(Validators.required)
    })
  }

  trackByIdx(index: number, _item: any) {
    return index
  }

  /**
   * When days list value changes check if the value is everyday and if true select all values
   * @param event The event object from p-multiSelect onChange: { value: T[], itemValue?: T }
   * @param restriction Restriction for update form control value
   */
  dayListValueChanges(event: any, restriction: timeRange) {
    const formControl = restriction.formControl?.get('daysWeek')
    const currentSelectionObjects: any[] = event.value || []
    const clickedObject: any | undefined = event.itemValue

    let finalSelectionObjects: any[] = [...currentSelectionObjects]

    const everydayObject = this.dayList.find((day) => day.value === -1)
    const hasEveryday = finalSelectionObjects.some((day) => (typeof day === 'object' ? day.value === -1 : day === -1))

    if (clickedObject && clickedObject.value === -1) {
      if (hasEveryday) {
        finalSelectionObjects = everydayObject ? [everydayObject.value] : []
      }
    } else if (clickedObject && clickedObject.value !== -1) {
      if (hasEveryday) {
        finalSelectionObjects = finalSelectionObjects.filter((day) => (typeof day === 'object' ? day.value !== -1 : day !== -1))
      }
    }

    finalSelectionObjects = finalSelectionObjects || []

    const currentValueValues = (formControl?.value || []).map((d: any) => d.value).sort()
    const finalSelectionValues = finalSelectionObjects.map((d) => d.value).sort()

    if (JSON.stringify(currentValueValues) !== JSON.stringify(finalSelectionValues)) {
      formControl?.setValue(finalSelectionObjects, { emitEvent: false })
    }

    restriction.daysWeek = finalSelectionObjects

    if (finalSelectionObjects.length === 0) {
      formControl?.markAsTouched()
    } else if (formControl?.hasError('required')) {
      formControl?.updateValueAndValidity({ emitEvent: false })
    }
  }

  /**
   * Ob dates changed method
   * @param dates
   */
  onDatesChange(dates: any, formControl: FormControl) {
    for (const key in dates) {
      formControl.get(key)?.setValue(dates[key])
    }
  }

  /**
   * Get detailed information about restriction collisions for user feedback
   * @returns Array of collision details or empty array if no collisions
   */
  getCollisionDetails(): Array<{ restrictionId1: number; restrictionId2: number; conflictingDays: string[]; timeOverlap: string }> {
    const collisions: Array<{ restrictionId1: number; restrictionId2: number; conflictingDays: string[]; timeOverlap: string }> = []

    if (!this.selectedRestrictions || this.selectedRestrictions.length < 2) {
      return collisions
    }

    for (let i = 0; i < this.selectedRestrictions.length; i++) {
      for (let j = i + 1; j < this.selectedRestrictions.length; j++) {
        const restriction1 = this.normalizeRestriction(this.selectedRestrictions[i])
        const restriction2 = this.normalizeRestriction(this.selectedRestrictions[j])

        if (this.restrictionsHaveTimeConflict(restriction1, restriction2)) {
          const conflictingDays = this.getConflictingDays(restriction1, restriction2)
          const timeOverlap = this.getTimeOverlapDescription(restriction1, restriction2)

          collisions.push({
            restrictionId1: restriction1.restrictionId,
            restrictionId2: restriction2.restrictionId,
            conflictingDays,
            timeOverlap,
          })
        }
      }
    }

    return collisions
  }

  /**
   * Get the days that conflict between two restrictions
   * @param restriction1 First restriction
   * @param restriction2 Second restriction
   * @returns Array of conflicting day names
   */
  private getConflictingDays(restriction1: timeRange, restriction2: timeRange): string[] {
    const days1 = restriction1.daysWeek.map((day: any) => (typeof day === 'object' ? day.value : day))
    const days2 = restriction2.daysWeek.map((day: any) => (typeof day === 'object' ? day.value : day))

    const conflictingDayValues = days1.filter((day: any) => days2.includes(day))
    return conflictingDayValues.map((dayValue: any) => {
      const dayObj = this.dayList?.find((d) => d.value === dayValue)
      return dayObj?.label || this.translate.instant('COMMON.FORMS.TIME_RESTRICTIONS.DAY_FALLBACK', { dayValue })
    })
  }

  /**
   * Get a description of the time overlap between two restrictions
   * @param restriction1 First restriction
   * @param restriction2 Second restriction
   * @returns Description string of the time overlap
   */ private getTimeOverlapDescription(restriction1: timeRange, restriction2: timeRange): string {
    const formatTime = (time: string | Date) => {
      if (isDate(time)) {
        return this.datePipe.transform(time, 'HH:mm') || ''
      }
      return time as string
    }

    const time1 = `${formatTime(restriction1.hourFirstRange)}-${formatTime(restriction1.hourSecondRange)}`
    const time2 = `${formatTime(restriction2.hourFirstRange)}-${formatTime(restriction2.hourSecondRange)}`

    return this.translate.instant('COMMON.FORMS.TIME_RESTRICTIONS.TIME_OVERLAP_FORMAT', { time1, time2 })
  }

  /**
   * Check if a specific restriction has any collisions
   * @param restriction The restriction to check
   * @returns true if this restriction has conflicts with others
   */
  hasRestrictionCollision(restriction: timeRange): boolean {
    return this.hasCollision(restriction)
  }

  /**
   * Get collision message for a specific restriction
   * @param restriction The restriction to get collision message for
   * @returns User-friendly collision message
   */ getRestrictionCollisionMessage(restriction: timeRange): string {
    const collisionDetails = this.getCollisionDetails()
    const restrictionCollisions = collisionDetails.filter(
      (collision) => collision.restrictionId1 === restriction.restrictionId || collision.restrictionId2 === restriction.restrictionId
    )

    if (restrictionCollisions.length === 0) {
      return ''
    }

    const collision = restrictionCollisions[0]
    return this.translate.instant('COMMON.FORMS.TIME_RESTRICTIONS.OVERLAPS_WITH', { days: collision.conflictingDays.join(', ') })
  }
}
